import { useEffect, useState, useMemo } from "react";
import Pagination from "@/components/dashboard/Pagination";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, MoreHorizontal, Edit, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Service {
  id: string;
  name: string;
  description: string | null;
  price: number;
  duration: number;
  active: boolean;
  sort_order: number;
  service_options: { id: string; name: string; price: number; duration: number }[];
}

const ServicesPage = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const paginatedServices = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return services.slice(start, start + PAGE_SIZE);
  }, [services, page]);

  const fetchServices = async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("services")
      .select("*, service_options(*)")
      .eq("organisation_id", profile.organisation_id)
      .order("sort_order", { ascending: true });
    setServices((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchServices();
  }, [profile?.organisation_id]);

  const moveService = async (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;

    const reordered = [...services];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    setServices(reordered);

    await Promise.all(
      reordered.map((s, i) =>
        supabase.from("services").update({ sort_order: i }).eq("id", s.id)
      )
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("services").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(`${deleteTarget.name} ${t("services.deleted")}`);
      setServices((prev) => prev.filter((s) => s.id !== deleteTarget.id));
    }
    setDeleteTarget(null);
  };

  const onNavigate = (id: string) => navigate(`/dashboard/services/${id}/edit`);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">{t("services.title")}</h1>
        <Button asChild>
          <Link to="/dashboard/services/new">
            <Plus className="h-4 w-4 mr-2" />
            {t("services.add")}
          </Link>
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <p className="text-muted-foreground mb-4">{t("services.no_items")}</p>
          <Button asChild>
            <Link to="/dashboard/services/new">
              <Plus className="h-4 w-4 mr-2" />
              {t("services.add")}
            </Link>
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop grid */}
          <div className="hidden md:block rounded-xl border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-[3rem_1fr_6rem_5rem_1fr_5rem_3rem] items-center gap-2 px-2 py-3 border-b border-border bg-muted/50">
              <div></div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("services.col_service")}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("services.col_price")}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("services.col_duration")}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("services.col_options")}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("services.col_status")}</span>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider text-right">{t("services.col_actions")}</span>
            </div>
            {paginatedServices.map((service, idx) => {
              const index = (page - 1) * PAGE_SIZE + idx;
              return (
              <div
                key={service.id}
                className="grid grid-cols-[3rem_1fr_6rem_5rem_1fr_5rem_3rem] items-center gap-2 px-2 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => onNavigate(service.id)}
              >
                <div className="flex flex-col items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    disabled={index === 0}
                    onClick={() => moveService(index, "up")}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                    disabled={index === services.length - 1}
                    onClick={() => moveService(index, "down")}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{service.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{service.description}</p>
                </div>
                <span className="text-sm font-semibold text-primary">€{Number(service.price).toFixed(2)}</span>
                <span className="text-sm text-foreground">{service.duration} min</span>
                <div>
                  {service.service_options.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {service.service_options.map((opt) => (
                        <Badge key={opt.id} variant="outline" className="text-[10px] font-normal">
                          {opt.name} (+€{Number(opt.price).toFixed(2)}, +{opt.duration}min)
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
                <Badge variant={service.active ? "default" : "secondary"} className="text-[10px] w-fit">
                  {service.active ? t("services.active") : t("services.inactive")}
                </Badge>
                <div className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild><Link to={`/dashboard/services/${service.id}/edit`} className="flex items-center"><Edit className="h-4 w-4 mr-2" /> {t("services.edit")}</Link></DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(service); }}><Trash2 className="h-4 w-4 mr-2" /> {t("services.delete")}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              );
            })}
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {paginatedServices.map((service, idx) => {
              const index = (page - 1) * PAGE_SIZE + idx;
              return (
              <div key={service.id} className="rounded-xl border border-border bg-card shadow-card p-4 cursor-pointer transition-colors" onClick={() => onNavigate(service.id)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 flex-1">
                    <div className="flex flex-col gap-0.5 mt-0.5" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        disabled={index === 0}
                        onClick={() => moveService(index, "up")}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        disabled={index === services.length - 1}
                        onClick={() => moveService(index, "down")}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground">{service.name}</h3>
                        {!service.active && <Badge variant="secondary" className="text-[10px]">{t("services.inactive")}</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{service.description}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild><Link to={`/dashboard/services/${service.id}/edit`} className="flex items-center"><Edit className="h-4 w-4 mr-2" /> {t("services.edit")}</Link></DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(service); }}><Trash2 className="h-4 w-4 mr-2" /> {t("services.delete")}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-semibold text-primary text-sm">€{Number(service.price).toFixed(2)}</span>
                  <span>{service.duration} min</span>
                </div>
              </div>
              );
            })}
          </div>

          <Pagination currentPage={page} totalItems={services.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("services.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("services.delete_desc_pre")} <span className="font-medium text-foreground">{deleteTarget?.name}</span>{t("services.delete_desc_post")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("services.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("services.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServicesPage;
